import React, { useState, useCallback, useEffect } from 'react'
import { Row, Col, Input, Form } from 'antd'
import PropTypes from 'prop-types'
// Local Files
import FiltersLayout from '../component/FiltersLayout'
import AddMore from '../component/AddMore'
import TagGroup from '../../../components/TagGroup'
import HandleInputTags from '../../../helpers/HandleInputTagsHelpers'
import { setIsOpen } from '../helpers/actionHelpers'
import FiltersCount from '../component/FiltersCount'
import { AddButton } from './styled'
import Title from 'antd/lib/typography/Title'

const Education = ({ existedFilters, setExistedFilters }) => {
  const [values, setValues] = useState({
    degreeValue: '',
    certificationsValue: '',
    excludeDegreeValue: '',
    excludeCertsValue: '',
    degree: [],
    certifications: [],
    excludedDegree: [],
    excludedCertifications: [],
    isExcludeDegree: false,
    isExcludeCerts: false,
    isOpen: false
  })

  // 🔹 Handle Add for Degree/Certifications
  const handleAdd = useCallback(
    (activeInputValue, activeInput) => {
      if (!values[activeInputValue]) return

      const tags = HandleInputTags(activeInputValue, activeInput, values)
      if (tags.length > 0 && values[activeInputValue]) {
        const updatedValues = {
          ...values,
          [activeInput]: tags,
          [activeInputValue]: '',
          isExcludeDegree: false,
          isExcludeCerts: false
        }
        setValues(updatedValues)

        const updatedEducationFilter = {
          ...existedFilters.education,
          degree: {
            ...(updatedValues.degree?.length ? { shouldHave: updatedValues.degree } : {}),
            ...(updatedValues.excludedDegree?.length
              ? { mustNotHave: updatedValues.excludedDegree }
              : {})
          },
          certifications: {
            ...(updatedValues.certifications?.length
              ? { shouldHave: updatedValues.certifications }
              : {}),
            ...(updatedValues.excludedCertifications?.length
              ? { mustNotHave: updatedValues.excludedCertifications }
              : {})
          }
        }

        setExistedFilters({
          ...existedFilters,
          education: updatedEducationFilter
        })
      }
    },
    [values, existedFilters, setExistedFilters]
  )

  // 🔹 Handle Delete
  const handleDelete = (tags, activeTagsType) => {
    const updatedValues = { ...values, [activeTagsType]: tags }
    setValues(updatedValues)

    const updatedEducationFilter = {
      ...existedFilters.education,
      degree: {
        ...(updatedValues.degree?.length ? { shouldHave: updatedValues.degree } : {}),
        ...(updatedValues.excludedDegree?.length
          ? { mustNotHave: updatedValues.excludedDegree }
          : {})
      },
      certifications: {
        ...(updatedValues.certifications?.length
          ? { shouldHave: updatedValues.certifications }
          : {}),
        ...(updatedValues.excludedCertifications?.length
          ? { mustNotHave: updatedValues.excludedCertifications }
          : {})
      }
    }

    setExistedFilters({
      ...existedFilters,
      education: updatedEducationFilter
    })
  }

  // 🔹 Clear Include/Exclude for Degree or Certs
  const clearIncludeDegree = () => {
    setValues({ ...values, degree: [] })
    const prev = existedFilters.education?.degree || {}
    const { shouldHave, ...rest } = prev
    setExistedFilters({
      ...existedFilters,
      education: { ...existedFilters.education, degree: rest }
    })
  }

  const clearExcludeDegree = () => {
    setValues({ ...values, excludedDegree: [] })
    const prev = existedFilters.education?.degree || {}
    const { mustNotHave, ...rest } = prev
    setExistedFilters({
      ...existedFilters,
      education: { ...existedFilters.education, degree: rest }
    })
  }

  const clearIncludeCerts = () => {
    setValues({ ...values, certifications: [] })
    const prev = existedFilters.education?.certifications || {}
    const { shouldHave, ...rest } = prev
    setExistedFilters({
      ...existedFilters,
      education: { ...existedFilters.education, certifications: rest }
    })
  }

  const clearExcludeCerts = () => {
    setValues({ ...values, excludedCertifications: [] })
    const prev = existedFilters?.education?.certifications || {}
    const { mustNotHave, ...rest } = prev
    setExistedFilters({
      ...existedFilters,
      education: { ...existedFilters?.education, certifications: rest }
    })
  }

  // 🔹 Sync existing filters to local state
  useEffect(() => {
    setValues((prev) => ({
      ...prev,
      degree: existedFilters?.education?.degree?.shouldHave || [],
      excludedDegree: existedFilters?.education?.degree?.mustNotHave || [],
      certifications: existedFilters?.education?.certifications?.shouldHave || [],
      excludedCertifications: existedFilters?.education?.certifications?.mustNotHave || []
    }))
  }, [existedFilters])

  const emptyFields = [
    values.degree,
    values.excludedDegree,
    values.certifications,
    values.excludedCertifications
  ]

  return (
    <FiltersLayout
      title="EDUCATION"
      setIsOpen={() => setIsOpen(setValues, values)}
      isOpen={values.isOpen}
    >
      {emptyFields.every((field) => field.length === 0) && !values.isOpen && (
        <Title level={4} disabled style={{ fontSize: '14px', marginBottom: 0 }}>
          All Education Levels
        </Title>
      )}
      {/* Degree Section */}
      <Row>
        <Form className="block-level-form">
          {(values.degree.length > 0 || values.isOpen) && (
            <FiltersCount
              title="Degree"
              ExcludeField={values.degree}
              clearExcludeFilters={clearIncludeDegree}
            />
          )}
          <Row className="tags-label filters-tags">
            <TagGroup tags={values.degree} onDelete={handleDelete} activeTagsType="degree" />
          </Row>
          {values.isOpen && (
            <Form.Item style={{ position: 'relative' }}>
              <Input
                name="degreeValue"
                placeholder="Add Degree"
                value={values.degreeValue}
                onChange={(e) => setValues({ ...values, [e.target.name]: e.target.value })}
                onPressEnter={() => handleAdd('degreeValue', 'degree')}
              />
              <AddButton
                onClick={() => handleAdd('degreeValue', 'degree')}
                noBorder
                maxWidth
                absolute
                noBackground
                disabled={!values.degreeValue}
              >
                Add
              </AddButton>
            </Form.Item>
          )}
        </Form>
      </Row>

      <Col className={values.excludedDegree.length || values.isOpen ? 'exclude-tags-area' : ''}>
        {(values.excludedDegree.length > 0 || values.isOpen) && (
          <FiltersCount
            title="Exclude Degrees"
            ExcludeField={values.excludedDegree}
            clearExcludeFilters={clearExcludeDegree}
          />
        )}
        <Row>
          <TagGroup
            tags={values.excludedDegree}
            isExlude={values.excludedDegree && true}
            onDelete={handleDelete}
            activeTagsType="excludedDegree"
          />
        </Row>
        {values.isExcludeDegree && values.isOpen && (
          <Row style={{ position: 'relative' }}>
            <Input
              name="excludeDegreeValue"
              placeholder="Exclude Degree"
              value={values.excludeDegreeValue}
              onChange={(e) => setValues({ ...values, [e.target.name]: e.target.value })}
              onPressEnter={() => handleAdd('excludeDegreeValue', 'excludedDegree')}
            />
            <AddButton
              onClick={() => handleAdd('excludeDegreeValue', 'excludedDegree')}
              noBorder
              maxWidth
              absolute
              noBackground
              disabled={!values.excludeDegreeValue}
            >
              Add
            </AddButton>
          </Row>
        )}
      </Col>

      {!values.isExcludeDegree && values.isOpen && (
        <AddMore
          title="Exclude Degree"
          onClick={() => setValues({ ...values, isExcludeDegree: true })}
        />
      )}

      {/* Certifications Section */}
      <Row className={values.certifications.length > 0 || values.isOpen ? 'margin-top-row' : ''}>
        <Form className="block-level-form">
          {(values.certifications.length > 0 || values.isOpen) && (
            <FiltersCount
              title="Certifications"
              ExcludeField={values.certifications}
              clearExcludeFilters={clearIncludeCerts}
            />
          )}
          <Row className="tags-label filters-tags">
            <TagGroup
              tags={values.certifications}
              onDelete={handleDelete}
              activeTagsType="certifications"
            />
          </Row>
          {values.isOpen && (
            <Form.Item style={{ position: 'relative' }}>
              <Input
                name="certificationsValue"
                placeholder="Add Certification"
                value={values.certificationsValue}
                onChange={(e) => setValues({ ...values, [e.target.name]: e.target.value })}
                onPressEnter={() => handleAdd('certificationsValue', 'certifications')}
              />
              <AddButton
                onClick={() => handleAdd('certificationsValue', 'certifications')}
                noBorder
                maxWidth
                absolute
                noBackground
                disabled={!values.certificationsValue}
              >
                Add
              </AddButton>
            </Form.Item>
          )}
        </Form>
      </Row>

      <Col
        className={values.excludedCertifications.length || values.isOpen ? 'exclude-tags-area' : ''}
      >
        {(values.excludedCertifications.length > 0 || values.isOpen) && (
          <FiltersCount
            title="Exclude Certifications"
            ExcludeField={values.excludedCertifications}
            clearExcludeFilters={clearExcludeCerts}
          />
        )}
        <Row>
          <TagGroup
            tags={values.excludedCertifications}
            isExlude={values.excludedCertifications && true}
            onDelete={handleDelete}
            activeTagsType="excludedCertifications"
          />
        </Row>
        {values.isExcludeCerts && values.isOpen && (
          <Row style={{ position: 'relative' }}>
            <Input
              name="excludeCertsValue"
              placeholder="Exclude Certification"
              value={values.excludeCertsValue}
              onChange={(e) => setValues({ ...values, [e.target.name]: e.target.value })}
              onPressEnter={() => handleAdd('excludeCertsValue', 'excludeedCertifications')}
            />
            <AddButton
              onClick={() => handleAdd('excludeCertsValue', 'excludedCertifications')}
              noBorder
              maxWidth
              absolute
              noBackground
              disabled={!values.excludeCertsValue}
            >
              Add
            </AddButton>
          </Row>
        )}
      </Col>

      {!values.isExcludeCerts && values.isOpen && (
        <AddMore
          title="Exclude Certification"
          onClick={() => setValues({ ...values, isExcludeCerts: true })}
        />
      )}
    </FiltersLayout>
  )
}

Education.propTypes = {
  setExistedFilters: PropTypes.func,
  existedFilters: PropTypes.object
}

export default Education
